import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle, XCircle, Loader2, ArrowLeft, CalendarDays, Clock, Edit3 } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS_AM = ["08:00","08:30","09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30"];
const SLOTS_PM = ["15:00","15:30","16:00","16:30","17:00","17:30"];

export default function ModifyAppointmentPage() {
  const { token } = useParams();
  const [apt, setApt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // "success" | "error"
  const [error, setError] = useState("");
  const [configData, setConfigData] = useState(null);

  const [newDate, setNewDate] = useState(null);
  const [newTimeSlot, setNewTimeSlot] = useState("");
  const [comment, setComment] = useState("");
  const [minDateInfo, setMinDateInfo] = useState(null); // {min_date, governing_service_name, applied_delay_label}
  const [runtimeError, setRuntimeError] = useState(""); // Iter 191 — error tras submit (sin perder la página)

  const phone = configData?.phone || "916003800";

  useEffect(() => {
    axios.get(`${API.replace('/api','')}/api/config`).then(r => setConfigData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/appointments/action/${token}`)
      .then(res => { setApt(res.data.appointment); })
      .catch(() => { setError("Enlace invalido o expirado."); })
      .finally(() => setLoading(false));
  }, [token]);

  // Iter 191 — Calcular fecha mínima según servicio(s) de la cita
  useEffect(() => {
    if (!apt) return;
    const slugs = [];
    if (Array.isArray(apt.services)) {
      for (const s of apt.services) if (s?.slug) slugs.push(s.slug);
    }
    if (slugs.length === 0 && apt.service_slug) slugs.push(apt.service_slug);
    if (slugs.length === 0) return; // sin info de servicio, no aplicamos restricción adicional
    axios.post(`${API}/appointment-services/min-date`, {
      service_slugs: slugs,
      center: apt.center || "",
    }).then(r => setMinDateInfo(r.data)).catch(() => setMinDateInfo(null));
  }, [apt]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments/modify/${token}`, {
        new_date: newDate ? format(newDate, "yyyy-MM-dd") : "",
        new_time_slot: newTimeSlot,
        comment,
      });
      setResult("success");
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      // Si el backend bloquea por plazo mínimo, mostrar mensaje claro
      if (msg) setRuntimeError(msg);
      setResult("error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="modify-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-2">Enlace no valido</h2>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (result === "success") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="modify-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="heading-industrial text-2xl font-bold text-slate-900 uppercase mb-3">Solicitud Recibida</h2>
          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            Hemos recibido tu solicitud de modificación. Nuestro equipo revisará la disponibilidad y se pondrá en contacto contigo para confirmar los cambios.
          </p>
          <p className="text-sm text-slate-500 mb-8">
            Telefono de atencion: <strong>{phone}</strong>
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-6" data-testid="modify-go-home">Volver al inicio</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (result === "error") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="modify-failed">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="heading-industrial text-xl font-bold text-slate-900 uppercase mb-2">No se pudo procesar el cambio</h2>
          <p className="text-sm text-slate-600 mb-6" data-testid="modify-error-detail">{runtimeError || `No se pudo enviar la solicitud. Intentalo de nuevo o llama al ${phone}.`}</p>
          <Button onClick={() => { setResult(null); setRuntimeError(""); }} variant="outline" className="rounded-sm text-xs uppercase tracking-wider font-semibold">Reintentar</Button>
        </div>
      </div>
    );
  }

  const isCancelled = apt?.status?.includes("cancelada");

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-lg mx-auto px-4" data-testid="modify-page">
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Modificar cita</span>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-2 uppercase">Solicitar cambio de cita</h1>
        </div>

        <Card className="border-slate-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            {isCancelled ? (
              <div className="text-center py-4">
                <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <p className="text-sm text-slate-600">Esta cita esta cancelada y no se puede modificar.</p>
              </div>
            ) : (
              <>
                {/* Current appointment info */}
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-6">
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Cita actual</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <span className="text-slate-500">Cliente</span>
                    <span className="font-medium text-slate-900" data-testid="modify-client-name">{apt?.client_name}</span>
                    <span className="text-slate-500">Matricula</span>
                    <span className="font-mono font-semibold text-slate-900">{apt?.plate}</span>
                    <span className="text-slate-500">Vehiculo</span>
                    <span className="font-medium text-slate-900">{apt?.brand} {apt?.model_name}</span>
                    <span className="text-slate-500">Centro</span>
                    <span className="font-medium text-slate-900">{apt?.center_name}</span>
                    <span className="text-slate-500">Servicio</span>
                    <span className="font-medium text-slate-900">{apt?.service_type}</span>
                    <span className="text-slate-500">Fecha actual</span>
                    <span className="font-medium text-slate-900">{apt?.date}</span>
                    <span className="text-slate-500">Hora actual</span>
                    <span className="font-mono font-medium text-slate-900">{apt?.time_slot}</span>
                  </div>
                </div>

                {/* Modification form */}
                <div className="space-y-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Edit3 className="w-4 h-4 text-red-600" />
                    <span className="text-xs uppercase tracking-wider font-semibold text-slate-900">Cambios solicitados</span>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                      <CalendarDays className="w-3 h-3 inline mr-1" /> Nueva fecha deseada
                    </Label>
                    {minDateInfo?.min_date && (
                      <div className="text-[11px] text-slate-700 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="modify-min-date-info">
                        Por el tipo de servicio seleccionado, la primera fecha disponible es <strong>{format(new Date(minDateInfo.min_date + 'T00:00:00'), "EEEE d 'de' MMMM yyyy", { locale: es })}</strong>
                        {minDateInfo.applied_delay_label ? <> · <span className="text-slate-500">{minDateInfo.applied_delay_label}{minDateInfo.governing_service_name ? ` — ${minDateInfo.governing_service_name}` : ''}</span></> : null}.
                      </div>
                    )}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left rounded-sm h-10 border-slate-200 font-normal" data-testid="modify-date-trigger">
                          <CalendarDays className="w-4 h-4 mr-2 text-slate-400" />
                          {newDate ? format(newDate, "EEEE d 'de' MMMM yyyy", { locale: es }) : "Selecciona nueva fecha"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={newDate}
                          onSelect={setNewDate}
                          disabled={(d) => {
                            // Bloquear pasado y domingos
                            if (d.getDay() === 0) return true;
                            const today = new Date(); today.setHours(0, 0, 0, 0);
                            if (d < today) return true;
                            // Bloquear si está antes del plazo mínimo del servicio
                            if (minDateInfo?.min_date) {
                              const min = new Date(minDateInfo.min_date + 'T00:00:00');
                              if (d < min) return true;
                            }
                            return false;
                          }}
                          locale={es}
                          data-testid="modify-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                      <Clock className="w-3 h-3 inline mr-1" /> Nueva hora deseada
                    </Label>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Manana</p>
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                        {SLOTS_AM.map(t => (
                          <button key={t} type="button" onClick={() => setNewTimeSlot(t)}
                            className={`text-xs font-mono py-2 rounded-sm border transition-colors ${newTimeSlot === t ? 'bg-red-600 text-white border-red-600' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`}
                            data-testid={`modify-slot-${t}`}>{t}</button>
                        ))}
                      </div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 mt-4">Tarde</p>
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                        {SLOTS_PM.map(t => (
                          <button key={t} type="button" onClick={() => setNewTimeSlot(t)}
                            className={`text-xs font-mono py-2 rounded-sm border transition-colors ${newTimeSlot === t ? 'bg-red-600 text-white border-red-600' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`}
                            data-testid={`modify-slot-${t}`}>{t}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Comentario (opcional)</Label>
                    <Textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Motivo del cambio o cualquier indicacion..."
                      className="rounded-sm border-slate-200 min-h-[80px]"
                      data-testid="modify-comment"
                    />
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                    <strong>Nota:</strong> La modificación no es automática. Nuestro equipo revisará tu solicitud y se pondrá en contacto contigo para confirmar los cambios.
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Link to="/" className="flex-1">
                    <Button variant="outline" className="w-full rounded-sm text-xs uppercase tracking-wider font-semibold h-10" data-testid="modify-cancel-btn">
                      Cancelar
                    </Button>
                  </Link>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || (!newDate && !newTimeSlot && !comment)}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-bold h-10"
                    data-testid="modify-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar solicitud"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Telefono de atencion: <strong>{phone}</strong>
        </p>
      </div>
    </div>
  );
}
